import React, { useState, useEffect } from 'react';
import { getDevices, getDrivers, updateDriver } from '../traccar';
import { getLoads, putLoad, withStatus, loadTotal, LOAD_STATUSES, STATUS_COLORS } from '../loadStore';
import { printDispatchSheet } from '../dispatchSheet';
import { playOpen, playClose } from '../clickSound';
import SopPanel from '../SopPanel';
import { loadCritical, loadGlanceFlags, billingChecklist, DELAY_REASONS, reasonKind, issueBrokerEmail, issueCompanyEmail } from '../sop';
import { buildAlarmReport, openAlarmReportEmail, getRecipients, saveRecipients, isEmail } from '../alarmReport';
import { getOutbox, pendingCount, setStatus as setOutboxStatus, clearDone, enqueue } from '../outbox';
import { getTrailers } from '../trailerStore';
import { createTripForLoad } from '../tripStore';

const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const dstr = (s) => (s ? new Date(s).toLocaleDateString() : '—');
const inp = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' };

// stages shown as pipeline columns (Invoiced kept off the board)
const COLUMNS = ['Available', 'Dispatched', 'At Pickup', 'Loaded', 'In Transit', 'At Delivery', 'Delivered', 'Complete'];
const ACTIVE_GLANCE = ['Dispatched', 'At Pickup', 'Loaded', 'In Transit', 'At Delivery'];
const ALERT_META = {
  Late: { icon: '🕒', color: '#b91c1c', bg: '#fee2e2' },
  'Reefer temp': { icon: '🌡️', color: '#b91c1c', bg: '#fee2e2' },
  'Detention risk': { icon: '⏱️', color: '#b45309', bg: '#fef3c7' },
  Breakdown: { icon: '🔧', color: '#b91c1c', bg: '#fee2e2' },
  'Accident / Incident': { icon: '🚨', color: '#b91c1c', bg: '#fee2e2' },
  Rejection: { icon: '⛔', color: '#b91c1c', bg: '#fee2e2' },
  'Transfer load': { icon: '⇄', color: '#b45309', bg: '#fef3c7' },
  'Rescue load': { icon: '🆘', color: '#b91c1c', bg: '#fee2e2' },
};
const alertMeta = (kind) => ALERT_META[kind] || { icon: '⚠️', color: '#b45309', bg: '#fef3c7' };
const nextOf = (s) => { const i = LOAD_STATUSES.indexOf(s); return LOAD_STATUSES[Math.min(i + 1, LOAD_STATUSES.length - 1)]; };
const prevOf = (s) => { const i = LOAD_STATUSES.indexOf(s); return LOAD_STATUSES[Math.max(i - 1, 0)]; };

export default function DispatchPage() {
  const [loads, setLoads] = useState([]);
  const [devices, setDevices] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dispatch, setDispatch] = useState(null); // load being dispatched
  const [detail, setDetail] = useState(null); // load whose timeline is open
  const [sop, setSop] = useState(null); // load whose SOP procedure panel is open
  const [alarm, setAlarm] = useState(null); // alarm report preview
  const [recip, setRecip] = useState(getRecipients()); // editable alarm recipients
  const [editRecip, setEditRecip] = useState(false);
  const [issue, setIssue] = useState(null); // load whose issue modal is open
  const [issueForm, setIssueForm] = useState({ reason: '', note: '', location: '', byDriver: true });
  const [customReasons, setCustomReasons] = useState(() => { try { return JSON.parse(localStorage.getItem('dd-delay-reasons')) || []; } catch { return []; } });
  const [newReason, setNewReason] = useState('');
  const [outboxOpen, setOutboxOpen] = useState(false);
  const [outbox, setOutbox] = useState(getOutbox());
  const [pending, setPending] = useState(pendingCount());
  const refreshOutbox = () => { setOutbox(getOutbox()); setPending(pendingCount()); };
  const [trailers, setTrailers] = useState([]);
  const [assign, setAssign] = useState({ vehicleId: '', driverId: '', coDriverId: '', trailerId: '', tripNumber: '', billNumber: '' });

  const refresh = async () => setLoads(await getLoads());
  useEffect(() => {
    refresh();
    getTrailers().then(setTrailers).catch(() => {});
    getDevices().then(setDevices).catch(() => {});
    getDrivers().then(setDrivers).catch(() => {});
    const t = setInterval(() => { refresh(); refreshOutbox(); }, 15000);
    const t2 = setInterval(refreshOutbox, 5000);
    return () => { clearInterval(t); clearInterval(t2); };
  }, []);

  const vName = (id) => (devices.find((d) => String(d.id) === String(id)) || {}).name;
  const dName = (id) => (drivers.find((d) => String(d.id) === String(id)) || {}).name;

  const changeStatus = async (load, status) => {
    await putLoad(withStatus(load, status));
    refresh();
  };

  // assign a driver to a unit with cumulative logs (mirrors Loads/Drivers behavior)
  const assignDriverToUnit = async (driverId, deviceId) => {
    const drv = drivers.find((d) => String(d.id) === String(driverId));
    if (!drv || !deviceId) return;
    const now = new Date().toISOString();
    const A = { ...(drv.attributes || {}) };
    const assignments = JSON.parse(JSON.stringify(A.assignments || {}));
    if (A.currentUnit && assignments[A.currentUnit]) {
      const openLog = assignments[A.currentUnit].logs.find((l) => !l.end);
      if (openLog) openLog.end = now;
    }
    const dev = devices.find((d) => String(d.id) === String(deviceId));
    if (!assignments[deviceId]) assignments[deviceId] = { name: dev ? dev.name : `Unit ${deviceId}`, logs: [] };
    assignments[deviceId].logs.push({ start: now, end: null });
    const updated = { ...drv, attributes: { ...A, assignments, currentUnit: deviceId } };
    await updateDriver(updated);
    setDrivers((ds) => ds.map((d) => (d.id === drv.id ? updated : d)));
  };

  const openDispatch = (load) => {
    playOpen();
    setAssign({ vehicleId: load.vehicleId || '', driverId: load.driverId || '', coDriverId: '', trailerId: load.trailerId || '', tripNumber: load.tripNumber || '', billNumber: load.billNumber || '' });
    setDispatch(load);
  };

  const confirmDispatch = async () => {
    if (!assign.driverId || !assign.vehicleId || !assign.trailerId) { alert('Assign a driver, a truck and a trailer to create the trip.'); return; }
    // driver reassignment check
    const existing = drivers.find((d) => String((d.attributes || {}).currentUnit) === String(assign.vehicleId) && String(d.id) !== String(assign.driverId));
    if (existing) {
      const ok = window.confirm(`${existing.name} is currently on ${vName(assign.vehicleId)}. Reassign this unit to ${dName(assign.driverId)}?`);
      if (!ok) return;
    }
    await assignDriverToUnit(assign.driverId, assign.vehicleId);
    if (assign.coDriverId) await assignDriverToUnit(assign.coDriverId, assign.vehicleId);

    const trailer = trailers.find((t) => String(t.id) === String(assign.trailerId));
    const driverIds = [assign.driverId, assign.coDriverId].filter(Boolean);
    // Assignment creates the TRIP (execution plan). Load carries the freight; trip carries the movement.
    const base = { ...dispatch, billNumber: assign.billNumber || dispatch.billNumber, trailerNumber: trailer ? trailer.unit : dispatch.trailerNumber };
    const { trip, load: withTrip } = await createTripForLoad(base, {
      truckId: assign.vehicleId, driverIds, trailerId: assign.trailerId, tripNumber: assign.tripNumber || undefined,
    });
    const updated = withStatus({ ...withTrip, tripNumber: trip.tripNumber }, 'Dispatched');
    await putLoad(updated);
    setDispatch(null);
    playClose();
    refresh();
    setTimeout(() => { if (window.confirm(`Trip ${trip.tripNumber} created. Print the driver dispatch sheet now?`)) printDispatchSheet(updated, dName(assign.driverId), vName(assign.vehicleId)); }, 100);
  };

  // ---- outbox: approve & send a queued autopilot action ----
  const sendAction = (it) => {
    if (it.channel === 'broker' || it.channel === 'alarm') {
      const url = `mailto:?subject=${encodeURIComponent(it.subject)}&body=${encodeURIComponent(it.body)}`;
      window.open(url, '_blank');
    } else if (it.channel === 'company') {
      const to = recip.to.map((x) => x.contact).filter(isEmail).join(',');
      const cc = recip.cc.map((x) => x.contact).filter(isEmail).join(',');
      window.open(`mailto:${to}?cc=${cc}&subject=${encodeURIComponent(it.subject)}&body=${encodeURIComponent(it.body)}`, '_blank');
    } else if (it.channel === 'driver') {
      navigator.clipboard.writeText(it.body).catch(() => {});
    }
    setOutboxStatus(it.id, 'sent');
    refreshOutbox();
  };
  const skipAction = (it) => { setOutboxStatus(it.id, 'skipped'); refreshOutbox(); };
  const approveAll = () => { outbox.filter((i) => i.status === 'pending').forEach(sendAction); };

  // ---- load issues / delays (driver texts in a problem) ----
  const openIssue = (load) => {
    playOpen();
    const ex = load.issue && load.issue.active ? load.issue : null;
    setIssueForm({ reason: ex ? ex.reason : (customReasons[0] || DELAY_REASONS[0]), note: ex ? ex.note || '' : '', location: ex ? ex.location || '' : '', byDriver: ex ? ex.reportedBy === 'driver' : true });
    setIssue(load);
  };
  const addCustomReason = () => {
    const r = newReason.trim(); if (!r) return;
    if (!customReasons.includes(r)) { const next = [...customReasons, r]; setCustomReasons(next); localStorage.setItem('dd-delay-reasons', JSON.stringify(next)); }
    setIssueForm((f) => ({ ...f, reason: r })); setNewReason('');
  };
  const submitIssue = async () => {
    if (!issueForm.reason) { alert('Pick or add a reason for the issue.'); return; }
    const l = issue;
    const at = new Date().toISOString();
    const iss = { active: true, reason: issueForm.reason, note: issueForm.note, location: issueForm.location, reportedBy: issueForm.byDriver ? 'driver' : 'dispatch', at, resolved: false };
    const updated = {
      ...l, issue: iss,
      critical: { active: true, kind: reasonKind(issueForm.reason), note: issueForm.reason + (issueForm.note ? ' — ' + issueForm.note : ''), at },
      statusHistory: [...(l.statusHistory || []), { status: l.status, at, note: `⚠️ Issue reported (${iss.reportedBy}): ${issueForm.reason}` }],
    };
    await putLoad(updated);
    const ctx = { truckName: vName(l.vehicleId), driverName: dName(l.driverId), location: issueForm.location };
    const be = issueBrokerEmail(updated, ctx);
    const ce = issueCompanyEmail(updated, ctx);
    enqueue([
      { sig: `${l.id}:issue-broker:${at}`, loadId: l.id, billNumber: l.billNumber, channel: 'broker', kind: `Issue — ${issueForm.reason}`, subject: be.subject, body: be.body, to: (l.caller && l.caller.name) || 'broker' },
      { sig: `${l.id}:issue-company:${at}`, loadId: l.id, billNumber: l.billNumber, channel: 'company', kind: `Issue notice — ${issueForm.reason}`, subject: ce.subject, body: ce.body, to: recip.to.map((x) => x.name).join(', ') },
    ]);
    setIssue(null); playClose(); refresh(); refreshOutbox();
  };
  const resolveIssue = async () => {
    const l = issue;
    await putLoad({ ...l, issue: { ...(l.issue || {}), active: false, resolved: true, resolvedAt: new Date().toISOString() }, critical: { active: false } });
    setIssue(null); playClose(); refresh();
  };

  // ---- alarm recipients editing ----
  const updateRecip = (list, i, field, val) => {
    const next = { ...recip, [list]: recip[list].map((r, j) => (j === i ? { ...r, [field]: val } : r)) };
    setRecip(next);
  };
  const addRecip = (list) => setRecip({ ...recip, [list]: [...recip[list], { name: '', contact: '' }] });
  const removeRecip = (list, i) => setRecip({ ...recip, [list]: recip[list].filter((_, j) => j !== i) });
  const persistRecip = () => { saveRecipients(recip); setEditRecip(false); if (alarm) setAlarm(buildAlarmReport()); };

  // Priority-1 / Critical — loads with detention risk or a manual critical flag
  const critical = loads
    .map((l) => ({ load: l, flags: loadCritical(l) }))
    .filter((x) => x.flags.length > 0);

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1>🚦 Dispatch</h1>
          <p>Move each load through its stages — dispatch a driver, track the trip, complete on delivery.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => { playOpen(); refreshOutbox(); setOutboxOpen(true); }} style={{ position: 'relative', background: pending ? 'linear-gradient(135deg,#f97316,#f4511e)' : '#0f172a', color: 'white', fontWeight: 700, fontSize: '13px', padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
            🤖 Autopilot
            {pending > 0 && <span style={{ marginLeft: '6px', background: 'white', color: '#dc2626', borderRadius: '999px', padding: '1px 7px', fontSize: '12px', fontWeight: 800 }}>{pending}</span>}
          </button>
          <button onClick={() => { playOpen(); setAlarm(buildAlarmReport()); }} style={{ background: 'linear-gradient(135deg,#0a1a2f,#12325a)', color: 'white', fontWeight: 700, fontSize: '13px', padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>🔔 Alarm Report</button>
        </div>
      </div>

      {/* Priority 1 / Critical banner — everyone monitors this */}
      <div style={{ border: `2px solid ${critical.length ? '#dc2626' : '#e2e8f0'}`, background: critical.length ? '#fef2f2' : '#f8fafc', borderRadius: '12px', padding: '12px 14px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>{critical.length ? '🚨' : '🛡️'}</span>
          <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '14px', color: critical.length ? '#dc2626' : '#334155', flex: 1 }}>
            PRIORITY 1 / CRITICAL — Save the load before the operation
          </span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: critical.length ? '#dc2626' : '#64748b' }}>{critical.length} active</span>
        </div>
        {critical.length > 0 && (
          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {critical.map(({ load, flags }) => (
              <div key={load.id} onClick={() => { playOpen(); setSop(load); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'white', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer' }}>
                <span style={{ fontWeight: 800, color: '#0066ff', fontSize: '13px' }}>{load.billNumber}</span>
                <span style={{ fontSize: '12px', color: '#7f1d1d', flex: 1 }}>
                  {flags.map((f) => `${f.kind}: ${f.detail}`).join('  ·  ')}
                </span>
                <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>Open SOP ›</span>
              </div>
            ))}
          </div>
        )}
        {critical.length === 0 && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>All clear — no critical situations. Monitors: reefer temp, breakdowns, rejections, transfers, rescues, detention risk.</div>}
      </div>

      {/* pipeline */}
      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '12px' }}>
        {COLUMNS.map((col) => {
          const items = loads.filter((l) => l.status === col);
          return (
            <div key={col} style={{ minWidth: '250px', flexShrink: 0, background: '#f1f5f9', borderRadius: '12px', padding: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: STATUS_COLORS[col] || '#64748b' }}></span>
                <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: '#0f172a', flex: 1 }}>{col}</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', background: 'white', borderRadius: '999px', padding: '1px 8px' }}>{items.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {items.map((l) => (
                  <div key={l.id} style={{ background: 'white', borderRadius: '10px', padding: '10px', boxShadow: '0 1px 3px rgba(15,23,42,0.08)', borderLeft: `3px solid ${STATUS_COLORS[col] || '#64748b'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontWeight: 800, color: '#0066ff', fontSize: '13px', flex: 1 }}>{l.billNumber}</span>
                      <span style={{ fontSize: '12px', fontWeight: 700 }}>{money(loadTotal(l))}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#0f172a', margin: '4px 0', fontWeight: 600 }}>
                      {l.shipper.city || l.shipper.name || '—'} → {l.consignee.city || l.consignee.name || '—'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      🚛 {vName(l.vehicleId) || '—'} · 👤 {dName(l.driverId) || '—'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px' }}>PU {dstr(l.pickup.early)} · DEL {dstr(l.delivery.early)}</div>

                    {/* at-a-glance alerts for dispatched / in-transit loads */}
                    {ACTIVE_GLANCE.includes(l.status) && (() => {
                      const gf = loadGlanceFlags(l);
                      const seen = new Set();
                      const uniq = gf.filter((f) => { if (seen.has(f.kind)) return false; seen.add(f.kind); return true; });
                      if (uniq.length === 0) return <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 700, marginBottom: '6px' }}>✓ On track</div>;
                      return (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
                          {uniq.map((f, k) => { const m = alertMeta(f.kind); return (
                            <span key={k} title={f.detail} style={{ fontSize: '10px', fontWeight: 800, color: m.color, background: m.bg, borderRadius: '999px', padding: '2px 7px' }}>{m.icon} {f.kind}</span>
                          ); })}
                        </div>
                      );
                    })()}

                    {/* ready-to-bill glance for finished loads */}
                    {(l.status === 'Delivered' || l.status === 'Complete') && (() => {
                      const bc = billingChecklist(l);
                      return bc.ready
                        ? <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 800, marginBottom: '6px' }}>✅ Ready to bill</div>
                        : <div style={{ fontSize: '10px', color: '#b45309', fontWeight: 800, marginBottom: '6px' }} title={bc.missing.map((m) => m.label).join(', ')}>📋 {bc.missing.length} missing before billing</div>;
                    })()}

                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {l.status === 'Available'
                        ? <button onClick={() => openDispatch(l)} style={{ background: 'linear-gradient(135deg, #f97316, #f4511e)', color: 'white', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px' }}>🚦 Dispatch</button>
                        : <>
                          <button title="Back a stage" onClick={() => changeStatus(l, prevOf(l.status))} style={{ background: '#e2e8f0', color: '#334155', fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '6px' }}>‹</button>
                          <button title="Advance a stage" onClick={() => changeStatus(l, nextOf(l.status))} style={{ background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px' }}>Advance ›</button>
                        </>}
                      <button onClick={() => { playOpen(); setSop(l); }} title="SOP procedure — broker & driver messages, status code, checklist" style={{ background: '#ecfdf5', color: '#047857', fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '6px' }}>📋</button>
                      <button onClick={() => printDispatchSheet(l, dName(l.driverId), vName(l.vehicleId))} title="Print dispatch sheet" style={{ background: '#eef2ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '6px' }}>📄</button>
                      <button onClick={() => { playOpen(); setDetail(l); }} title="Trip timeline" style={{ background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '6px' }}>🕑</button>
                      {l.status !== 'Available' && (
                        <button onClick={() => openIssue(l)} title="Report an issue / delay — notify broker + company" style={{ background: (l.issue && l.issue.active) ? '#fee2e2' : '#fff7ed', color: (l.issue && l.issue.active) ? '#b91c1c' : '#c2410c', fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '6px' }}>{(l.issue && l.issue.active) ? '⚠️ Issue' : '⚠️'}</button>
                      )}
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', padding: '10px 0' }}>—</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* dispatch modal */}
      {dispatch && (
        <div onClick={() => setDispatch(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(460px, 96vw)', boxSizing: 'border-box', padding: '22px' }}>
            <h3 style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>🚦 Dispatch {dispatch.billNumber}</h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>{dispatch.shipper.city || dispatch.shipper.name} → {dispatch.consignee.city || dispatch.consignee.name}</p>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>🚛 Assign truck</span>
              <select style={inp} value={assign.vehicleId} onChange={(e) => setAssign({ ...assign, vehicleId: e.target.value })}>
                <option value="">— choose a unit —</option>
                {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>🚚 Assign trailer</span>
              <select style={inp} value={assign.trailerId} onChange={(e) => setAssign({ ...assign, trailerId: e.target.value })}>
                <option value="">— choose a trailer —</option>
                {trailers.map((t) => <option key={t.id} value={t.id}>{t.unit}{t.type ? ` · ${t.type}` : ''}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>👤 Assign driver</span>
              <select style={inp} value={assign.driverId} onChange={(e) => setAssign({ ...assign, driverId: e.target.value })}>
                <option value="">— choose a driver —</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.uniqueId})</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>👥 Co-driver (team, optional)</span>
              <select style={inp} value={assign.coDriverId} onChange={(e) => setAssign({ ...assign, coDriverId: e.target.value })}>
                <option value="">— none —</option>
                {drivers.filter((d) => String(d.id) !== String(assign.driverId)).map((d) => <option key={d.id} value={d.id}>{d.name} ({d.uniqueId})</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
              <div style={{ flex: 1 }}><span style={lbl}>Load #</span><input style={{ ...inp, background: '#f8fafc' }} value={assign.billNumber} onChange={(e) => setAssign({ ...assign, billNumber: e.target.value })} placeholder={dispatch.billNumber} /></div>
              <div style={{ flex: 1 }}><span style={lbl}>Trip #</span><input style={{ ...inp, background: '#f8fafc' }} value={assign.tripNumber} onChange={(e) => setAssign({ ...assign, tripNumber: e.target.value })} placeholder="auto-generated" /></div>
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px' }}>Both numbers are created by the system — leave blank to auto-assign. Edit only to override.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600 }} onClick={() => setDispatch(null)}>Cancel</button>
              <button style={{ background: 'linear-gradient(135deg, #f97316, #f4511e)', color: 'white', padding: '8px 16px', borderRadius: '6px', fontWeight: 700 }} onClick={confirmDispatch}>Create trip & dispatch</button>
            </div>
          </div>
        </div>
      )}

      {/* trip timeline */}
      {detail && (
        <div onClick={() => { playClose(); setDetail(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(440px, 96vw)', boxSizing: 'border-box', padding: '22px' }}>
            <h3 style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>🕑 {detail.billNumber} — Trip Timeline</h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>Current: <b>{detail.status}</b></p>
            {(detail.statusHistory || []).length === 0 && <p className="text-muted" style={{ fontSize: '13px' }}>No stage changes recorded yet.</p>}
            {(detail.statusHistory || []).slice().reverse().map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: STATUS_COLORS[h.status] || '#64748b', marginTop: '4px', flexShrink: 0 }}></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{h.status}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{new Date(h.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
            <div style={{ textAlign: 'right', marginTop: '14px' }}>
              <button style={{ background: '#eef2ff', color: '#1d4ed8', padding: '8px 14px', borderRadius: '6px', fontWeight: 700 }} onClick={() => printDispatchSheet(detail, dName(detail.driverId), vName(detail.vehicleId))}>📄 Dispatch sheet</button>
            </div>
          </div>
        </div>
      )}

      {/* Autopilot Outbox */}
      {outboxOpen && (
        <div onClick={() => { playClose(); setOutboxOpen(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(640px, 96vw)', boxSizing: 'border-box', padding: '20px', margin: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h3 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', flex: 1 }}>🤖 Autopilot — Outbox</h3>
              {pending > 0 && <button onClick={approveAll} style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white', fontWeight: 700, fontSize: '12px', padding: '7px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>✓ Approve & send all ({pending})</button>}
            </div>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>The dispatcher composes each message the moment a load changes status. You approve; sending becomes automatic once email/SMS is connected.</p>

            {outbox.length === 0 && <p style={{ fontSize: '13px', color: '#94a3b8' }}>Nothing queued yet. As loads move through their stages, drafts appear here.</p>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '60vh', overflowY: 'auto' }}>
              {outbox.slice(0, 40).map((it) => {
                const icon = it.channel === 'broker' ? '✉️' : it.channel === 'driver' ? '📱' : '🔔';
                const done = it.status !== 'pending';
                return (
                  <div key={it.id} style={{ border: '1px solid #e2e8f0', borderLeft: `4px solid ${it.status === 'sent' ? '#22c55e' : it.status === 'skipped' ? '#94a3b8' : '#f97316'}`, borderRadius: '10px', padding: '10px 12px', opacity: done ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span>{icon}</span>
                      <span style={{ fontWeight: 800, color: '#0066ff', fontSize: '13px' }}>{it.billNumber}</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', background: '#f1f5f9', borderRadius: '999px', padding: '1px 8px' }}>{it.kind}</span>
                      <span style={{ fontSize: '11px', color: '#94a3b8', flex: 1, textAlign: 'right' }}>→ {it.to}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>{it.subject}</div>
                    <div style={{ fontSize: '12px', color: '#0f172a', whiteSpace: 'pre-wrap', background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: '6px', padding: '8px', maxHeight: '120px', overflowY: 'auto' }}>{it.body}</div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px', justifyContent: 'flex-end' }}>
                      {it.status === 'pending' ? (
                        <>
                          <button onClick={() => skipAction(it)} style={{ background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: 700, padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>Skip</button>
                          <button onClick={() => sendAction(it)} style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white', fontSize: '11px', fontWeight: 700, padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>✓ Approve & send</button>
                        </>
                      ) : (
                        <span style={{ fontSize: '11px', fontWeight: 700, color: it.status === 'sent' ? '#16a34a' : '#94a3b8' }}>{it.status === 'sent' ? '✓ Sent' : 'Skipped'}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '14px' }}>
              <button onClick={() => { clearDone(); refreshOutbox(); }} style={{ background: '#f1f5f9', color: '#475569', padding: '8px 14px', borderRadius: '6px', fontWeight: 600, border: 'none', cursor: 'pointer', fontSize: '12px' }}>Clear sent/skipped</button>
              <button onClick={() => { playClose(); setOutboxOpen(false); }} style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Issue / delay modal */}
      {issue && (
        <div onClick={() => { playClose(); setIssue(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(480px, 96vw)', boxSizing: 'border-box', padding: '22px' }}>
            <h3 style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>⚠️ Report an issue — {issue.billNumber}</h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>Flags the load for a dispatcher and drafts an update to the broker + your company contacts.</p>

            <div style={{ marginBottom: '10px' }}><span style={lbl}>What's the issue?</span>
              <select style={inp} value={issueForm.reason} onChange={(e) => setIssueForm({ ...issueForm, reason: e.target.value })}>
                {[...customReasons, ...DELAY_REASONS].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
              <input style={{ ...inp, flex: 1 }} placeholder="Add another reason…" value={newReason} onChange={(e) => setNewReason(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustomReason(); }} />
              <button style={{ background: '#eef2ff', color: '#1d4ed8', fontWeight: 700, fontSize: '12px', padding: '0 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }} onClick={addCustomReason}>+ Add</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}><span style={lbl}>Location (optional)</span><input style={inp} value={issueForm.location} onChange={(e) => setIssueForm({ ...issueForm, location: e.target.value })} placeholder="e.g. I-75 near Macon, GA" /></div>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>Details (optional)</span><textarea style={{ ...inp, minHeight: '54px' }} value={issueForm.note} onChange={(e) => setIssueForm({ ...issueForm, note: e.target.value })} placeholder="What happened / what's the plan…" /></div>
            <label style={{ fontSize: '12px', color: '#334155', display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '14px', cursor: 'pointer' }}>
              <input type="checkbox" checked={issueForm.byDriver} onChange={(e) => setIssueForm({ ...issueForm, byDriver: e.target.checked })} /> Reported by the driver
            </label>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              {issue.issue && issue.issue.active
                ? <button onClick={resolveIssue} style={{ background: '#dcfce7', color: '#15803d', padding: '9px 16px', borderRadius: '8px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>✓ Resolve issue</button>
                : <span />}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => { playClose(); setIssue(null); }} style={{ background: '#e2e8f0', color: '#334155', padding: '9px 16px', borderRadius: '8px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Cancel</button>
                <button onClick={submitIssue} style={{ background: 'linear-gradient(135deg,#f97316,#f4511e)', color: 'white', padding: '9px 16px', borderRadius: '8px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>🚨 Flag & notify</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SOP procedure panel */}
      {sop && (
        <SopPanel
          load={sop}
          driverName={dName(sop.driverId)}
          vehicleName={vName(sop.vehicleId)}
          onClose={() => setSop(null)}
          onChange={refresh}
        />
      )}

      {/* Alarm Report preview */}
      {alarm && (
        <div onClick={() => { playClose(); setAlarm(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(520px, 96vw)', boxSizing: 'border-box', padding: '20px', margin: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <h3 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', flex: 1 }}>🔔 {alarm.subject}</h3>
              <button onClick={() => setEditRecip((v) => !v)} style={{ background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: 700, padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>{editRecip ? 'Done' : '✎ Recipients'}</button>
            </div>

            {!editRecip ? (
              <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 12px' }}>
                To: <b>{recip.to.map((x) => x.name + (x.contact ? ` <${x.contact}>` : '')).join(', ') || '—'}</b> · CC: <b>{recip.cc.map((x) => x.name + (x.contact ? ` <${x.contact}>` : '')).join(', ') || '—'}</b>
              </p>
            ) : (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', marginBottom: '12px' }}>
                {['to', 'cc'].map((list) => (
                  <div key={list} style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{list === 'to' ? 'To' : 'CC'}</div>
                    {recip[list].map((r, i) => {
                      const tag = r.contact ? (isEmail(r.contact) ? '✉️' : '📱') : '';
                      return (
                        <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '5px', alignItems: 'center' }}>
                          <input value={r.name} onChange={(e) => updateRecip(list, i, 'name', e.target.value)} placeholder="Name" style={{ flex: '0 0 34%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', minWidth: 0 }} />
                          <input value={r.contact} onChange={(e) => updateRecip(list, i, 'contact', e.target.value)} placeholder="email or phone #" style={{ flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', minWidth: 0 }} />
                          <span style={{ fontSize: '13px', width: '18px', textAlign: 'center' }} title={isEmail(r.contact) ? 'Email — sent by mail' : (r.contact ? 'Phone — sent by SMS' : '')}>{tag}</span>
                          <button onClick={() => removeRecip(list, i)} style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', width: '24px', height: '26px', cursor: 'pointer', fontWeight: 700 }}>×</button>
                        </div>
                      );
                    })}
                    <button onClick={() => addRecip(list)} style={{ background: '#eef2ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>+ Add {list === 'to' ? 'recipient' : 'CC'}</button>
                  </div>
                ))}
                <button onClick={persistRecip} style={{ background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontSize: '12px', fontWeight: 700, padding: '7px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer', marginTop: '4px' }}>Save recipients</button>
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#334155', marginBottom: '8px' }}>Alarms between <b>{alarm.label}</b>:</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#e8eef7' }}><th style={{ textAlign: 'left', padding: '6px 8px', border: '1px solid #cbd5e1' }}>ALARM (TRAILER #)</th><th style={{ textAlign: 'left', padding: '6px 8px', border: '1px solid #cbd5e1' }}>RESOLVED</th></tr></thead>
              <tbody>
                {alarm.rows.length === 0 && <tr><td colSpan={2} style={{ padding: '8px', color: '#64748b', border: '1px solid #cbd5e1' }}>No alarms in this window</td></tr>}
                {alarm.rows.map((r, i) => (
                  <tr key={i}><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>{r.alarm}<div style={{ fontSize: '11px', color: '#94a3b8' }}>{r.title}</div></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>{r.resolved}</td></tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>First report of the day (6:30 AM) is sent by night shift; follow-ups reply in the same thread. Add recipient emails in settings to enable one-click send.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, border: 'none', cursor: 'pointer' }} onClick={() => { playClose(); setAlarm(null); }}>Close</button>
              <button style={{ background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', padding: '8px 16px', borderRadius: '6px', fontWeight: 700, border: 'none', cursor: 'pointer' }} onClick={() => openAlarmReportEmail()}>✉️ Draft email</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
